import { useState, useEffect, useRef, useCallback } from 'react';
import { useMqttCamera } from '@/hooks/useMqttCamera';
import { mqttService } from '@/services/mqttService';

interface CameraControlProps {
  deviceId: string;
}

const ANGLE_MIN = 60;
const ANGLE_MAX = 120;
const ANGLE_DEFAULT = 90;
const TICK_MARKS = [60, 75, 90, 105, 120];
const PRESETS = [
  { label: '좌60°', value: 60 },
  { label: '좌75°', value: 75 },
  { label: '정면90°', value: 90 },
  { label: '우105°', value: 105 },
  { label: '우120°', value: 120 },
];

export default function CameraControl({ deviceId }: CameraControlProps) {
  const {
    rgb1Angle,
    rgb2Angle,
    isOnline,
    connected,
    connectionError,
    sendCommand,
    setRgb1Angle,
    setRgb2Angle,
  } = useMqttCamera(deviceId);

  const [localRgb1, setLocalRgb1] = useState(ANGLE_DEFAULT);
  const [localRgb2, setLocalRgb2] = useState(ANGLE_DEFAULT);
  const [toastMsg, setToastMsg] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalRgb1(rgb1Angle);
  }, [rgb1Angle]);

  useEffect(() => {
    setLocalRgb2(rgb2Angle);
  }, [rgb2Angle]);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2500);
  }, []);

  const handleImmediate = useCallback(
    (camera: 'rgb1' | 'rgb2', angle: number) => {
      if (!isOnline) {
        showToast('장비가 오프라인 상태입니다.');
        return;
      }
      const clamped = Math.round(Math.max(ANGLE_MIN, Math.min(ANGLE_MAX, angle)));
      if (camera === 'rgb1') {
        setLocalRgb1(clamped);
        setRgb1Angle(clamped);
      } else {
        setLocalRgb2(clamped);
        setRgb2Angle(clamped);
      }
      mqttSendImmediate(camera, clamped);
    },
    [isOnline, showToast, setRgb1Angle, setRgb2Angle],
  );

  const mqttSendImmediate = useCallback(
    (camera: 'rgb1' | 'rgb2', angle: number) => {
      mqttService.sendServoCommand(camera, angle);
    },
    [],
  );

  const handleSliderChange = useCallback(
    (camera: 'rgb1' | 'rgb2', value: number) => {
      if (!isOnline) return;
      const clamped = Math.round(Math.max(ANGLE_MIN, Math.min(ANGLE_MAX, value)));
      if (camera === 'rgb1') {
        setLocalRgb1(clamped);
        setRgb1Angle(clamped);
      } else {
        setLocalRgb2(clamped);
        setRgb2Angle(clamped);
      }
      sendCommand(camera, clamped);
    },
    [isOnline, sendCommand, setRgb1Angle, setRgb2Angle],
  );

  if (!connected && !connectionError) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2" style={{ backgroundColor: '#f0f9f4' }}>
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-gamepad-line text-green-600"></i>
          </div>
          <span className="font-bold text-gray-800 text-sm">카메라 각도 제어</span>
          <div className="w-4 h-4 flex items-center justify-center ml-auto">
            <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>
          </div>
        </div>
        <div className="p-8 text-center text-gray-400 text-sm">
          MQTT 브로커에 연결 중...
        </div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2" style={{ backgroundColor: '#fef2f2' }}>
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-gamepad-line text-red-500"></i>
          </div>
          <span className="font-bold text-gray-800 text-sm">카메라 각도 제어</span>
          <span className="ml-auto text-xs text-red-500 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
            연결 실패
          </span>
        </div>
        <div className="p-6 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-red-50">
            <i className="ri-wifi-off-line text-red-400 text-xl"></i>
          </div>
          <p className="text-gray-500 text-sm mb-1">MQTT 브로커에 연결할 수 없습니다.</p>
          <p className="text-gray-400 text-xs">{connectionError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div
        className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2"
        style={{ backgroundColor: '#f0f9f4' }}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-gamepad-line text-green-600"></i>
        </div>
        <span className="font-bold text-gray-800 text-sm">카메라 각도 제어</span>
        <div className="ml-auto flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
              isOnline
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-600'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full inline-block ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}
            ></span>
            {isOnline ? '장비 온라인' : '장비 오프라인'}
          </span>
        </div>
      </div>

      {/* Offline Overlay */}
      {!isOnline && (
        <div className="absolute inset-0 z-10 flex items-center justify-center" style={{ top: 49 }}>
          <div className="absolute inset-0 bg-white/60 rounded-b-2xl"></div>
          <div className="relative z-20 bg-white/90 backdrop-blur-sm rounded-xl px-6 py-4 border border-gray-200 text-center max-w-xs mx-4">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-red-50">
              <i className="ri-wifi-off-line text-red-400 text-xl"></i>
            </div>
            <p className="text-gray-700 text-sm font-semibold mb-1">장비가 오프라인 상태입니다</p>
            <p className="text-gray-500 text-xs">타이머 활성 시간에만 제어 가능합니다.</p>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-5 py-2.5 rounded-xl shadow-lg whitespace-nowrap animate-pulse">
          {toastMsg}
        </div>
      )}

      {/* Camera Panels */}
      <div className={`p-4 md:p-5 ${!isOnline ? 'opacity-30 pointer-events-none' : ''}`}>
        <div className="flex flex-col md:flex-row gap-5">
          <CameraPanel
            label="LEFT RGB (RGB1)"
            icon="ri-camera-line"
            color="#27ae60"
            angle={localRgb1}
            disabled={!isOnline}
            onSliderChange={(v) => handleSliderChange('rgb1', v)}
            onAdjust={(delta) => handleImmediate('rgb1', localRgb1 + delta)}
            onPreset={(v) => handleImmediate('rgb1', v)}
          />
          <CameraPanel
            label="RIGHT RGB (RGB2)"
            icon="ri-camera-line"
            color="#e67e22"
            angle={localRgb2}
            disabled={!isOnline}
            onSliderChange={(v) => handleSliderChange('rgb2', v)}
            onAdjust={(delta) => handleImmediate('rgb2', localRgb2 + delta)}
            onPreset={(v) => handleImmediate('rgb2', v)}
          />
        </div>
      </div>
    </div>
  );
}

interface CameraPanelProps {
  label: string;
  icon: string;
  color: string;
  angle: number;
  disabled: boolean;
  onSliderChange: (value: number) => void;
  onAdjust: (delta: number) => void;
  onPreset: (value: number) => void;
}

function CameraPanel({
  label,
  icon,
  color,
  angle,
  disabled,
  onSliderChange,
  onAdjust,
  onPreset,
}: CameraPanelProps) {
  const sliderPercent = ((angle - ANGLE_MIN) / (ANGLE_MAX - ANGLE_MIN)) * 100;

  return (
    <div className="flex-1 min-w-0 bg-gray-50 rounded-xl p-4">
      {/* Label + Current Angle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
            style={{ backgroundColor: color + '18' }}
          >
            <i className={icon} style={{ color }}></i>
          </div>
          <span className="font-semibold text-gray-700 text-sm whitespace-nowrap">{label}</span>
        </div>
        <span
          className="text-lg font-bold whitespace-nowrap"
          style={{ color }}
        >
          {angle}°
        </span>
      </div>

      {/* Slider */}
      <div className="mb-3 px-1">
        <div className="relative">
          <input
            type="range"
            min={ANGLE_MIN}
            max={ANGLE_MAX}
            step={1}
            value={angle}
            disabled={disabled}
            onChange={(e) => onSliderChange(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${color}30 0%, ${color} ${sliderPercent}%, #e5e7eb ${sliderPercent}%, #e5e7eb 100%)`,
              accentColor: color,
            }}
          />
        </div>
        {/* Tick marks */}
        <div className="flex justify-between mt-1 px-0.5">
          {TICK_MARKS.map((tick) => (
            <span
              key={tick}
              className={`text-[10px] cursor-pointer whitespace-nowrap transition-colors ${
                angle === tick ? 'font-bold' : ''
              }`}
              style={{ color: angle === tick ? color : '#9ca3af' }}
              onClick={() => onSliderChange(tick)}
            >
              {tick}°
            </span>
          ))}
        </div>
      </div>

      {/* +/- Fine Adjust Buttons */}
      <div className="flex items-center justify-center gap-2 mb-3">
        <button
          onClick={() => onAdjust(-5)}
          disabled={disabled || angle <= ANGLE_MIN}
          className="w-9 h-9 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="-5°"
        >
          -5°
        </button>
        <button
          onClick={() => onAdjust(-1)}
          disabled={disabled || angle <= ANGLE_MIN}
          className="w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="-1°"
        >
          -1°
        </button>
        <span
          className="w-16 text-center font-bold text-lg whitespace-nowrap"
          style={{ color }}
        >
          {angle}°
        </span>
        <button
          onClick={() => onAdjust(1)}
          disabled={disabled || angle >= ANGLE_MAX}
          className="w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="+1°"
        >
          +1°
        </button>
        <button
          onClick={() => onAdjust(5)}
          disabled={disabled || angle >= ANGLE_MAX}
          className="w-9 h-9 flex items-center justify-center rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="+5°"
        >
          +5°
        </button>
      </div>

      {/* Preset Buttons */}
      <div className="flex gap-1.5 flex-wrap">
        {PRESETS.map((preset) => (
          <button
            key={preset.value}
            onClick={() => onPreset(preset.value)}
            disabled={disabled}
            className={`flex-1 min-w-0 px-2 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
              angle === preset.value
                ? 'text-white border-transparent'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
            } disabled:opacity-30 disabled:cursor-not-allowed`}
            style={
              angle === preset.value
                ? { backgroundColor: color, borderColor: color }
                : undefined
            }
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}